import { useState } from 'react'
import { Bell, BellOff } from 'lucide-react'
import { Switch } from '@/components/ui/switch'
import { Separator } from '@/components/ui/separator'
import {
  useUserPreferences,
  useUpdateUserPreferences,
  useSetRegulatoryDigestSubscription,
} from '@/hooks/queries'
import {
  isPushSupported,
  subscribeToPushNotifications,
  unsubscribeFromPush,
} from '@/lib/push'
import { useAuth } from '@/contexts/AuthContext'

export function NotificationsSection() {
  const { user } = useAuth()
  const userId = user?.sub ?? ''
  const { data: preferences } = useUserPreferences(userId)
  const updatePreferences = useUpdateUserPreferences()
  const setDigest = useSetRegulatoryDigestSubscription()

  const [pushSubscribed, setPushSubscribed] = useState(false)
  const [pushLoading, setPushLoading] = useState(false)
  const [pushError, setPushError] = useState<string | null>(null)

  async function handlePushToggle(enable: boolean) {
    setPushError(null)
    setPushLoading(true)
    try {
      if (enable) {
        const sub = await subscribeToPushNotifications()
        if (sub) {
          setPushSubscribed(true)
        } else {
          setPushError(
            Notification.permission === 'denied'
              ? 'Notifications are blocked in your browser. Allow them in site settings.'
              : 'Failed to subscribe. Please try again.',
          )
        }
      } else {
        await unsubscribeFromPush()
        setPushSubscribed(false)
      }
    } catch {
      setPushError('An error occurred. Please try again.')
    } finally {
      setPushLoading(false)
    }
  }

  const prefs = preferences ?? {
    criticalAlertsEnabled: true,
    emailEnabled: false,
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium">Critical alerts</p>
          <p className="text-xs text-muted-foreground">
            Receive alerts for critical severity items
          </p>
        </div>
        <Switch
          checked={prefs.criticalAlertsEnabled ?? true}
          onCheckedChange={(checked) =>
            updatePreferences.mutate({
              userId,
              criticalAlertsEnabled: checked,
            })
          }
        />
      </div>
      <Separator />
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium">Email briefing</p>
          <p className="text-xs text-muted-foreground">
            Daily email digest of top intelligence
          </p>
        </div>
        <Switch
          checked={prefs.emailEnabled ?? false}
          onCheckedChange={(checked) =>
            updatePreferences.mutate({ userId, emailEnabled: checked })
          }
        />
      </div>
      <Separator />
      <RegulatoryDigestRow
        optedOutAt={preferences ? (preferences.regulatoryDigestOptedOutAt ?? null) : undefined}
        pending={setDigest.isPending}
        failed={setDigest.isError}
        onChange={(enabled) => setDigest.mutate({ userId, enabled })}
      />
      <Separator />
      <div>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium">Critical alert push notifications</p>
            <p className="text-xs text-muted-foreground">
              Browser push alerts for critical severity items. Requires critical alerts to
              be enabled above.
            </p>
          </div>
          {isPushSupported() ? (
            <Switch
              checked={pushSubscribed}
              disabled={pushLoading}
              onCheckedChange={handlePushToggle}
            />
          ) : (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <BellOff className="h-4 w-4" />
              Not supported
            </div>
          )}
        </div>
        {pushSubscribed && !pushLoading && (
          <p className="mt-1.5 flex items-center gap-1 text-xs text-green-500">
            <Bell className="h-3.5 w-3.5" />
            Push notifications enabled
          </p>
        )}
        {pushError && <p className="mt-1.5 text-xs text-destructive">{pushError}</p>}
      </div>
    </div>
  )
}

/**
 * KAN-836 — the weekly regulatory digest. The one-click unsubscribe link in the
 * digest (KAN-810) can only turn it off; this signed-in control is the way to
 * turn it back on, and the unsubscribe page links here (`#notifications`).
 *
 * `optedOutAt`: `undefined` means there is no preferences record to write to
 * yet (the server refuses rather than create one), so the switch is disabled.
 * `null` means subscribed.
 */
function RegulatoryDigestRow({
  optedOutAt,
  pending,
  failed,
  onChange,
}: {
  optedOutAt: string | null | undefined
  pending: boolean
  failed: boolean
  onChange: (enabled: boolean) => void
}) {
  const known = optedOutAt !== undefined
  const subscribed = optedOutAt === null
  return (
    <div>
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-medium">Weekly regulatory digest</p>
          <p className="text-xs text-muted-foreground">
            An email each week on the regulatory frameworks you watch.
          </p>
        </div>
        <Switch
          checked={subscribed}
          disabled={!known || pending}
          onCheckedChange={onChange}
          aria-label="Weekly regulatory digest"
        />
      </div>
      {known && (
        <p className="mt-1.5 text-xs text-muted-foreground">
          {subscribed
            ? "You'll get the digest each week."
            : `${offSince(optedOutAt)} Turn it on to get the digest again.`}
        </p>
      )}
      {failed && (
        <p role="alert" className="mt-1.5 text-xs text-destructive">
          We couldn&apos;t save that change. Try again.
        </p>
      )}
    </div>
  )
}

function offSince(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return 'Off.'
  return `Off since ${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}.`
}
